import React, { useMemo, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { htmlInputTypeForValueKind, valueKindLabel } from "../../utils/fieldTypeRegistry";
import {
    sanitizeScalarOnChange,
    validateBetweenValue,
    validateListValue,
    validateScalarValue,
} from "../../utils/conditionValueInput";

const fsx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const TYPED_KINDS = new Set(["email", "phone", "uri", "uuid", "ipv4", "time", "number"]);

export function ValidatedScalarField({
    inputKind,
    value,
    onChange,
    label,
    helperText,
    ui = {},
    allowEmpty = true,
    showError = true,
}) {
    const [touched, setTouched] = useState(false);
    const validation = useMemo(
        () => validateScalarValue(inputKind, value, { allowEmpty }),
        [inputKind, value, allowEmpty],
    );
    const inputType = htmlInputTypeForValueKind(inputKind, ui);
    const displayError = showError && touched && !validation.valid;

    return (
        <TextField
            size="small"
            fullWidth
            type={inputType}
            label={label || valueKindLabel(inputKind) || "Valeur"}
            value={value ?? ""}
            onChange={(e) => onChange(sanitizeScalarOnChange(inputKind, e.target.value))}
            onBlur={() => setTouched(true)}
            error={displayError}
            helperText={displayError ? validation.error : helperText}
            InputLabelProps={inputType === "time" ? { shrink: true } : undefined}
            inputProps={
                inputKind === "phone"
                    ? { inputMode: "tel", autoComplete: "tel" }
                    : inputKind === "email"
                        ? { inputMode: "email", autoComplete: "email" }
                        : undefined
            }
            sx={fsx}
        />
    );
}

export function TypedChipListField({
    inputKind,
    value,
    onChange,
    label = "Valeurs",
    helperText,
    allowEmpty = true,
}) {
    const [inputValue, setInputValue] = useState("");
    const [touched, setTouched] = useState(false);
    const items = Array.isArray(value) ? value.map(String) : [];
    const listValidation = useMemo(
        () => validateListValue(inputKind, items, { allowEmpty }),
        [inputKind, items, allowEmpty],
    );
    const pendingValidation = useMemo(
        () => (inputValue.trim() ? validateScalarValue(inputKind, inputValue.trim(), { allowEmpty: false }) : { valid: true }),
        [inputKind, inputValue],
    );
    const displayError = touched && (!listValidation.valid || (inputValue.trim() && !pendingValidation.valid));
    const errorText = inputValue.trim() && !pendingValidation.valid
        ? pendingValidation.error
        : listValidation.error;

    const commitInput = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        const check = validateScalarValue(inputKind, trimmed, { allowEmpty: false });
        if (!check.valid) {
            setTouched(true);
            return;
        }
        const normalized = inputKind === "email"
            ? trimmed.toLowerCase()
            : inputKind === "phone"
                ? sanitizeScalarOnChange("phone", trimmed)
                : inputKind === "number"
                    ? Number(trimmed)
                    : trimmed;
        if (!items.some((x) => String(x) === String(normalized))) {
            onChange([...items, normalized]);
        }
        setInputValue("");
    };

    return (
        <Autocomplete
            multiple
            freeSolo={TYPED_KINDS.has(inputKind) || inputKind === "string"}
            options={[]}
            value={items}
            inputValue={inputValue}
            onInputChange={(_e, v, reason) => {
                if (reason === "reset") return;
                setInputValue(inputKind === "phone" ? sanitizeScalarOnChange("phone", v) : v);
            }}
            onChange={(_e, newValue) => {
                setTouched(true);
                const next = newValue.map((item) => {
                    const s = String(item).trim();
                    if (inputKind === "number") {
                        const n = Number(s);
                        return Number.isNaN(n) ? s : n;
                    }
                    if (inputKind === "email") return s.toLowerCase();
                    if (inputKind === "phone") return sanitizeScalarOnChange("phone", s);
                    return s;
                }).filter((item) => item !== "");
                onChange(next);
            }}
            onBlur={() => {
                setTouched(true);
                commitInput();
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const check = validateScalarValue(inputKind, option, { allowEmpty: false });
                    return (
                        <Chip
                            {...getTagProps({ index })}
                            key={`${option}-${index}`}
                            label={String(option)}
                            size="small"
                            color={check.valid ? "default" : "error"}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    label={label}
                    error={displayError}
                    helperText={displayError ? errorText : (helperText || "Entrée + Entrée pour ajouter")}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            commitInput();
                        }
                    }}
                    sx={fsx}
                />
            )}
        />
    );
}

export function ValidatedBetweenFields({
    inputKind,
    value,
    onChange,
    helperText,
    ui = {},
}) {
    const [touched, setTouched] = useState(false);
    const arr = Array.isArray(value) ? value : ["", ""];
    const validation = useMemo(
        () => validateBetweenValue(inputKind, arr, { allowEmpty: true }),
        [inputKind, arr],
    );
    const isNum = inputKind === "number";
    const isDate = inputKind === "date" || inputKind === "datetime";
    const isTime = inputKind === "time";
    const useTyped = TYPED_KINDS.has(inputKind);

    const fieldType = isTime ? "time" : isDate ? "date" : isNum ? "number" : (useTyped ? htmlInputTypeForValueKind(inputKind, ui) : "text");
    const displayError = touched && !validation.valid;

    const updateAt = (index, raw) => {
        const next = [...arr];
        let v = raw;
        if (isNum && raw !== "") v = Number(raw);
        if (useTyped && inputKind === "phone") v = sanitizeScalarOnChange("phone", raw);
        if (useTyped && inputKind === "email") v = String(raw).replace(/\s/g, "");
        next[index] = v;
        onChange(next);
    };

    return (
        <>
            <TextField
                size="small"
                fullWidth
                type={fieldType}
                label="De"
                value={arr[0] ?? ""}
                onChange={(e) => updateAt(0, e.target.value)}
                onBlur={() => setTouched(true)}
                error={displayError}
                InputLabelProps={(isDate || isTime) ? { shrink: true } : undefined}
                sx={{ ...fsx, flex: 1, minWidth: 120 }}
            />
            <TextField
                size="small"
                fullWidth
                type={fieldType}
                label="À"
                value={arr[1] ?? ""}
                onChange={(e) => updateAt(1, e.target.value)}
                onBlur={() => setTouched(true)}
                error={displayError}
                helperText={displayError ? validation.error : helperText}
                InputLabelProps={(isDate || isTime) ? { shrink: true } : undefined}
                sx={{ ...fsx, flex: 1, minWidth: 120 }}
            />
        </>
    );
}
